import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import { createActions } from '../../shared/actions';
import {
  alertCreator,
  failedAndSucceededNotIncluded
} from '../../shared/utils';
import {
  namespace,
  typeClearAlert,
  typeClearDialog,
  typeCreateTableData,
  typeDeleteTableData,
  typeEditTableData,
  typeExportTableData,
  typeHideDialog,
  typeInvalidToken,
  typeLoadCatalogues,
  typeLoadTableData,
  typeReloadTableData,
  typeShowAlert,
  typeShowDialog,
  typeVerifyToken,
} from './constants';

const clearAlert = createActions(typeClearAlert, namespace, {
  original: {
    payloadCreator({ id }) {
      return id;
    }
  },
  ...failedAndSucceededNotIncluded
});

const clearDialog = createActions(typeClearDialog, namespace, {
  original: {
    payloadCreator({ id }) {
      return id;
    }
  },
  ...failedAndSucceededNotIncluded
});

const createTableData = createActions(typeCreateTableData, namespace, {
  failed: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return {
          ...alertCreator(payload),
          ...payload.tableName && { tableName: payload.tableName }
        };
      }
    }
  },
  original: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return { tableName: payload.tableName };
      }
    },
    payloadCreator(payload) {
      if (isPlainObject(payload) && isPlainObject(payload.ajaxOptions)) {
        const { ajaxOptions, tableName, ...morePayload } = payload;
        return {
          ajaxOptions: {
            ...ajaxOptions,
            method: ajaxOptions.method || 'POST'
          },
          ...morePayload
        };
      }
    }
  },
  succeeded: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return {
          ...alertCreator(payload),
          ...payload.tableName && { tableName: payload.tableName }
        };
      }
    }
  }
});

const deleteTableData = createActions(typeDeleteTableData, namespace, {
  failed: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return {
          ...alertCreator(payload),
          ...payload.tableName && { tableName: payload.tableName }
        };
      }
    }
  },
  original: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return { tableName: payload.tableName };
      }
    },
    payloadCreator(payload) {
      if (isPlainObject(payload) && isPlainObject(payload.ajaxOptions)) {
        const { ajaxOptions, tableName, ...morePayload } = payload;
        return {
          ajaxOptions: {
            ...ajaxOptions,
            method: ajaxOptions.method || 'DELETE'
          },
          ...morePayload
        };
      }
    }
  },
  succeeded: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return {
          ...alertCreator(payload),
          ...payload.tableName && { tableName: payload.tableName }
        };
      }
    }
  }
});

const editTableData = createActions(typeEditTableData, namespace, {
  failed: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return {
          ...alertCreator(payload),
          ...payload.tableName && { tableName: payload.tableName }
        };
      }
    }
  },
  original: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return { tableName: payload.tableName };
      }
    },
    payloadCreator(payload) {
      if (isPlainObject(payload) && isPlainObject(payload.ajaxOptions)) {
        const { ajaxOptions, tableName, ...morePayload } = payload;
        return {
          ajaxOptions: {
            ...ajaxOptions,
            method: ajaxOptions.method || 'PUT'
          },
          ...morePayload
        };
      }
    }
  },
  succeeded: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return {
          ...alertCreator(payload),
          ...payload.tableName && { tableName: payload.tableName }
        };
      }
    }
  }
});

const exportTableData = createActions(typeExportTableData, namespace, {
  failed: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return {
          ...alertCreator(payload),
          ...payload.tableName && { tableName: payload.tableName }
        };
      }
    },
    payloadCreator(payload) {
      if (isPlainObject(payload)) {
        const { alert, tableName, ...morePayload } = payload;
        return morePayload;
      }
    }
  },
  original: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return { tableName: payload.tableName };
      }
    },
    payloadCreator(payload) {
      if (isPlainObject(payload) && isPlainObject(payload.ajaxOptions)) {
        const { ajaxOptions, tableName, ...morePayload } = payload;
        return {
          ajaxOptions,
          ...morePayload
        };
      }
    }
  },
  succeeded: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return {
          ...alertCreator(payload),
          ...payload.tableName && { tableName: payload.tableName }
        };
      }
    },
    payloadCreator(payload) {
      if (isPlainObject(payload)) {
        const { alert, tableName, ...morePayload } = payload;
        return morePayload;
      }
    }
  }
});

const hideDialog = createActions(typeHideDialog, namespace, {
  original: {
    payloadCreator({ id }) {
      return id;
    }
  },
  ...failedAndSucceededNotIncluded
});

const invalidToken = createActions(typeInvalidToken, namespace, {
  ...failedAndSucceededNotIncluded
});

const loadCatalogues = createActions(typeLoadCatalogues, namespace, {
  failed: { metaCreator: alertCreator },
  original: {
    payloadCreator(payload = {}) {
      const { ajaxOptions, ...morePayload } = payload;
      return {
        ajaxOptions: {
          ...ajaxOptions,
          endpoint: '/catalogs'
        },
        ...morePayload
      };
    }
  },
  succeeded: {
    payloadCreator(payload) {
      if (isPlainObject(payload)) {
        return payload;
      }
    }
  }
});

const loadTableData = createActions(typeLoadTableData, namespace, {
  failed: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return {
          ...alertCreator(payload),
          ...payload.tableName && { tableName: payload.tableName }
        };
      }
    },
    payloadCreator(payload) {
      if (isPlainObject(payload) && payload.event) {
        return { event: payload.event };
      }
    }
  },
  original: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return { tableName: payload.tableName };
      }
    },
    payloadCreator(payload) {
      if (isPlainObject(payload) && isPlainObject(payload.ajaxOptions)) {
        const { ajaxOptions, tableName, ...morePayload } = payload;
        return {
          ajaxOptions: {
            ...ajaxOptions,
            method: ajaxOptions.method || 'GET'
          },
          ...morePayload
        };
      }
    }
  },
  succeeded: {
    metaCreator(payload) {
      if (isPlainObject(payload)) {
        const { data, event, ...morePayload } = payload;
        return { ...morePayload };
      }
    },
    payloadCreator(payload) {
      if (isPlainObject(payload) && isPlainObject(payload.data)) {
        return {
          ...payload.data,
          ...payload.event && { event: payload.event }
        };
      }
    }
  }
});

const showAlert = createActions(typeShowAlert, namespace, {
  original: {
    payloadCreator(alert) {
      return alert;
    }
  },
  ...failedAndSucceededNotIncluded
});

const reloadTableData = createActions(typeReloadTableData, namespace, {
  failed: { include: false },
  original: {
    metaCreator(payload) {
      if (isPlainObject(payload) && isString(payload.tableName)) {
        return { tableName: payload.tableName };
      }
    }
  },
  succeeded: { include: false }
});

const showDialog = createActions(typeShowDialog, namespace, {
  original: {
    payloadCreator(dialog) {
      return dialog;
    }
  },
  ...failedAndSucceededNotIncluded
});

const verifyToken = createActions(typeVerifyToken, namespace, {
  original: {
    payloadCreator(token) {
      return token;
    }
  },
  ...failedAndSucceededNotIncluded
});

export default {
  ...clearAlert,
  ...clearDialog,
  ...createTableData,
  ...deleteTableData,
  ...editTableData,
  ...exportTableData,
  ...hideDialog,
  ...invalidToken,
  ...loadCatalogues,
  ...loadTableData,
  ...reloadTableData,
  ...showAlert,
  ...showDialog,
  ...verifyToken
};



// WEBPACK FOOTER //
// ./src/containers/App/actions.js